import EventDetails, {
  CustomLocationConstant,
  VirtualEventConstant
} from "../../../../stores/domainStore/pages/models/eventDetails";
import React, {useEffect, useState} from "react";
import {IconSource} from "../../../../shared/enums/iconSource";
import Banner from "../../../atoms/banner/banner";
import {observer} from "mobx-react-lite";
import {useTranslation} from "react-i18next";
import {useStores} from "../../../../stores/useStores";
import {Col, Container, Row} from "reactstrap";
import "./viewEventTemplate.sass";
import TextEdit from "../../../organisms/textEdit/textEdit";
import {EventDetailsHelper} from "../../../../shared/eventDetailsHelper";
import ViewField from "../../../molecules/viewField/viewField";
import EventStartAndEndView from "../../../organisms/eventStartAndEndView/eventStartAndEndView";
import {MultiVillageHelper} from "../../../../shared/multiVillageHelper";
import {EventStatus} from "../../../../shared/enums/eventStatus";
import {ICanCancelOrPostponeProps} from "../../../../shared/interfaces/sharedProps";
import {useVillageFeatureToggle} from "../../../../utils/featureToggle/villageFeatureToggle";
import {
  ProcessedBookingsFeatureName
} from "../../../../shared/constants";
import {EventCostAllocation} from "../../../../shared/enums/eventCostAllocation";
import {format, utcToZonedTime} from "date-fns-tz";

interface IViewEventTemplateProps extends ICanCancelOrPostponeProps {
  eventDetail: EventDetails;
  isLoading?: boolean;
  isViewingEventSeries?: boolean;
  isSaving?: boolean;
  isCancellingPostponing?: boolean;
  isCancelledOrPostponedEvent?: boolean;
  isReadOnly?: boolean;
}

const ViewEventTemplate: React.FC<IViewEventTemplateProps> = ({
  eventDetail,
  isSaving,
  isCancellingPostponing = false,
  isCancelledOrPostponedEvent = false,
  shouldShowCancelOrPostponeButton,
  openCancelPostponeModalAction,
  isReadOnly = false
}) => {
  const {t} = useTranslation(["translation", "event"]);
  const {
    domainStore: {eventsDomainStore, eventCatalogueDomainStore},
    uiStore: {eventsUiStore, accountStore}
  } = useStores();

  const showEventOccurrenceBanner =
    eventsDomainStore.occurrenceDate && eventDetail.isRecurringEvent;
  const showCreatedByResidentExperienceTeam =
    eventDetail.isMultiVillage &&
    !accountStore.abilities.can("Create", "MultiVillage") &&
    !eventDetail.isPassedEndDate;

  useEffect(() => {
    eventsDomainStore.getVillages(eventsUiStore.selectedCalendarName);
  }, [eventsDomainStore, eventsDomainStore.editableEvent, eventsUiStore.selectedCalendarName]);

  const [src, setSrc] = useState("");

  useEffect(() => {
    setSrc("");
    const fileId = eventDetail.image.fileId;
    if (!fileId) {
      return;
    }
    let isCurrent = true;
    eventCatalogueDomainStore
      .getEventTemplateImage(fileId, 110, 110)
      .then((result: string) => {
        if (isCurrent) {
          setSrc(result);
        }
      });
    return () => {
      isCurrent = false;
    };
  }, [eventCatalogueDomainStore, eventDetail.image.fileId]);
  const bookingLabel = (): string => {
    if (eventDetail.bookingDetails.hasBookingDeadline) {
      return t("event:labels:booking:hasBookingDeadline");
    }
    return t("event:labels:booking:noBooking");
  };

  const bookingDeadLineLabel = (): string => {
    if (eventDetail.bookingDeadlineDate) {
      return `${format(
        utcToZonedTime(
          eventDetail.bookingDeadlineDate,
          eventDetail.eventDateDetails.timeZone
        ),
        "iiii dd/MM/yyyy h:mm aaa"
      )}`;
    }
    return "";
  };

  const releaseDateLabel = (releaseDate: Date): string => {
    return `${format(
      utcToZonedTime(releaseDate, eventDetail.eventDateDetails.timeZone),
      "iiii dd/MM/yyyy h:mm aaa"
    )}`;
  };

  function getPlaceholder(eventStatus: EventStatus): string {
    switch (eventStatus) {
      case EventStatus.Cancelled:
        return t("event:placeholders.cancel");
      case EventStatus.Postponed:
        return t("event:placeholders.postpone");
      default:
        return "";
    }
  }

  const isTransactionsFeatureEnabled = useVillageFeatureToggle(
    ProcessedBookingsFeatureName
  );
  const isEnhancedCalendar = eventsUiStore.isEnhancedCalendar;
  const shouldSeeExtraFields = isEnhancedCalendar;

  const costAllocationText = (() => {
    switch (eventDetail.priceDetails.costAllocation.selectedItem?.value) {
      case EventCostAllocation.EnhancedActivities:
        return t("event:labels.costAllocation.enhancedActivities");
      case EventCostAllocation.Bar:
        return t("event:labels.costAllocation.bar");
      case EventCostAllocation.FineDining:
        return t("event:labels.costAllocation.fineDining");
      default:
        return "-";
    }
  })();

  return (
    <>
      <div
        className="view-event-template"
        data-testid="viewEventCancelPostpone"
      >
        {!shouldShowCancelOrPostponeButton && isCancelledOrPostponedEvent && (
          <>
            <Row>
              <Col className="editMargin event-cancel-postpone-reason">
                <TextEdit
                  type="textarea"
                  value={eventDetail.reason}
                  validationMessage={t("event:errors.reason")}
                  label={t("event:labels.reason")}
                  placeholder={getPlaceholder(eventDetail.eventStatus)}
                />
              </Col>
            </Row>
            <Row className="form-separator editMargin" />
          </>
        )}
        {showCreatedByResidentExperienceTeam &&
          !isCancelledOrPostponedEvent && (
            <div className="banner-margin">
              <Banner
                text={t("event:labels.eventCreatedByResidentExperienceTeam")}
                icon={IconSource.info}
              />
            </div>
          )}
        {showEventOccurrenceBanner && !isCancelledOrPostponedEvent && (
          <Row>
            <Col>
              <div className={"banner-margin"}>
                <Banner
                  text={t(
                    "event:messages.editingOrViewingOccurrenceOfEventSeries"
                  )}
                  icon={IconSource.info}
                />
              </div>
            </Col>
          </Row>
        )}
        {EventDetailsHelper.isCancelledOrPostponedEvent(eventDetail) &&
          // this flag is to determine this is a past event and show label
          !isCancelledOrPostponedEvent && (
            <>
              <Row className="editMargin">
                <Col>
                  <ViewField
                    label={t("event:labels.reason")}
                    value={eventDetail.reason.text}
                  />
                </Col>
              </Row>
            </>
          )}
        <Row className="editMargin">
          <Container fluid>
            <Row>
              <Col>
                <ViewField
                  label={t("event:labels.title")}
                  value={eventDetail.title.text}
                />
              </Col>
              <Col>
                <ViewField
                  value={eventDetail.category.selectedItem?.label}
                  label={t("event:labels.category")}
                />
              </Col>
            </Row>
          </Container>
          <Col />
        </Row>
        <Row className="editMargin">
          <Col>
            <ViewField
              label={
                isEnhancedCalendar
                  ? t("event:labels.featured")
                  : t("event:labels.special")
              }
              value={
                eventDetail.isFeatured.value
                  ? t("event:labels.yes")
                  : t("event:labels.no")
              }
            />
          </Col>
        </Row>
        <div className="form-separator" />
        {eventDetail.isMultiVillage && (
          <>
            <Row className="editMargin">
              <Col>
                <ViewField
                  label={t("event:labels.villages")}
                  value={MultiVillageHelper.VillageEllipsis(
                    t,
                    eventDetail.villages.selectedItems.map((v) => v.label),
                    accountStore.abilities.can("Create", "MultiVillage"),
                    null
                  )}
                />
              </Col>
            </Row>
            <Row className="form-separator editMargin" />
          </>
        )}

        <Row className="editMargin">
          <Col>
            <EventStartAndEndView
              eventDetails={eventDetail}
              isDisabled={isSaving}
              isCancellingPostponing={isCancellingPostponing}
              openCancelPostponeModalAction={openCancelPostponeModalAction}
              shouldShowCancelOrPostponeButton={
                isCancelledOrPostponedEvent
                  ? shouldShowCancelOrPostponeButton
                  : false
              }
              isEditingSingleOccurrence={
                eventsDomainStore.occurrenceDate !== undefined
              }
              isEndTimeVisible={isEnhancedCalendar}
            />
          </Col>
          {isReadOnly && eventDetail.releaseDate.date && isEnhancedCalendar && (
            <Col>
              <ViewField
                value={releaseDateLabel(eventDetail.releaseDate.date)}
                label={t("event:labels:preReleaseDate")}
              />
            </Col>
          )}
        </Row>

        <div className="form-separator" />

        {shouldSeeExtraFields && (
          <Row className="editMargin">
            <Col>
              <ViewField
                value={eventDetail.location.selectedItem?.label}
                label={t("event:labels.location")}
              />
            </Col>
            <Col>
              {eventDetail.location.selectedItem?.value ===
                CustomLocationConstant && (
                <ViewField
                  value={eventDetail.customLocation.text}
                  label={t("event:labels.customLocation")}
                />
              )}
              {eventDetail.location.selectedItem?.value ===
                VirtualEventConstant && (
                <ViewField label={t("event:labels.virtualLocation")}>
                  <a
                    href={eventDetail.virtualLocation.text}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {eventDetail.virtualLocation.text}
                  </a>
                </ViewField>
              )}
            </Col>
          </Row>
        )}
        {shouldSeeExtraFields && (
          <>
            <div className="form-separator" />
            <Row>
              <Col className="editMargin">
                <ViewField
                  value={EventDetailsHelper.getEventPriceText(t, eventDetail)}
                  label={t("event:labels.price.pricing")}
                />
              </Col>
              {isTransactionsFeatureEnabled.hasFeature.value &&
                eventDetail.priceDetails.costAllocation.selectedItem && (
                  <Col className="editMargin">
                    <ViewField
                      value={costAllocationText}
                      label={t("event:labels.price.costAllocation")}
                    />
                  </Col>
                )}
            </Row>
            <div className="form-separator" />
          </>
        )}

        <Row className="editMargin">
          <Container fluid>
            <Row>
              <Col>
                <ViewField
                  value={bookingLabel()}
                  label={t("event:labels:booking.bookingRequirement")}
                />
              </Col>
              {isReadOnly && isEnhancedCalendar && (
                <Col>
                  <ViewField
                    value={bookingDeadLineLabel()}
                    label={t("event:labels:booking.deadline")}
                  />
                </Col>
              )}
            </Row>
          </Container>
        </Row>

        {shouldSeeExtraFields && (
          <>
            <div className="form-separator" />
            <Row>
              <Col className="editMargin">
                <ViewField
                  value={eventDetail.description.text ?? ""}
                  label={t("event:labels.description")}
                />
              </Col>
            </Row>
            <Row>
              <Col className="editMargin">
                <ViewField label={t("event:labels.image")}>
                  <img className={"value-image"} alt="" src={src ?? ""} />
                </ViewField>
              </Col>
            </Row>
          </>
        )}
      </div>
    </>
  );
};

export default observer(ViewEventTemplate);
